/**
 * Income Statement Redux Slice
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { IncomeStatement, LineItem } from '../features/Financials/types/financials.types'
import * as incomeStatementService from '../features/Financials/services/incomeStatementService'

// ============================================================================
// State Interface
// ============================================================================

interface IncomeStatementState {
	entities: Record<string, IncomeStatement> // Keyed by `locationId`
	isLoading: boolean
	isInitialized: boolean
	error: string | null
	lastSaved: Record<string, number> // Last save timestamp (milliseconds) per income statement
}

const initialState: IncomeStatementState = {
	entities: {},
	isLoading: false,
	isInitialized: false,
	error: null,
	lastSaved: {},
}

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetch income statement for a location/project
 */
export const fetchIncomeStatement = createAsyncThunk(
	'incomeStatement/fetch',
	async ({ locationId }: { locationId: string }) => {
		const incomeStatement = await incomeStatementService.getIncomeStatement(locationId)
		return { locationId, incomeStatement }
	},
)

/**
 * Create a new income statement
 */
export const createIncomeStatement = createAsyncThunk(
	'incomeStatement/create',
	async ({
		locationId,
		incomeStatement,
	}: {
		locationId: string
		incomeStatement: Partial<IncomeStatement>
	}) => {
		const created = await incomeStatementService.createIncomeStatement(locationId, incomeStatement)
		return { locationId, incomeStatement: created }
	},
)

/**
 * Update income statement
 */
export const updateIncomeStatement = createAsyncThunk(
	'incomeStatement/update',
	async ({
		locationId,
		incomeStatement,
	}: {
		locationId: string
		incomeStatement: IncomeStatement
	}) => {
		const response = await incomeStatementService.updateIncomeStatement(locationId, incomeStatement)
		return {
			locationId,
			incomeStatement: response.incomeStatement,
			timestamp: response.timestamp,
		}
	},
)

/**
 * Update a single cell
 */
export const updateCell = createAsyncThunk(
	'incomeStatement/updateCell',
	async ({
		locationId,
		lineItemId,
		quarterId,
		value,
	}: {
		locationId: string
		lineItemId: string
		quarterId: string
		value: number
	}) => {
		const response = await incomeStatementService.updateCell(
			locationId,
			lineItemId,
			quarterId,
			value,
		)
		return {
			locationId,
			lineItemId,
			quarterId,
			value,
			incomeStatement: response.incomeStatement,
			timestamp: response.timestamp,
		}
	},
)

/**
 * Add a line item
 */
export const addLineItem = createAsyncThunk(
	'incomeStatement/addLineItem',
	async ({ locationId, lineItem }: { locationId: string; lineItem: Partial<LineItem> }) => {
		const updated = await incomeStatementService.addLineItem(locationId, lineItem)
		return { locationId, incomeStatement: updated }
	},
)

/**
 * Remove a line item
 */
export const removeLineItem = createAsyncThunk(
	'incomeStatement/removeLineItem',
	async ({ locationId, lineItemId }: { locationId: string; lineItemId: string }) => {
		const updated = await incomeStatementService.removeLineItem(locationId, lineItemId)
		return { locationId, incomeStatement: updated }
	},
)

/**
 * Reorder line items
 */
export const reorderLineItems = createAsyncThunk(
	'incomeStatement/reorderLineItems',
	async ({ locationId, lineItemIds }: { locationId: string; lineItemIds: string[] }) => {
		const updated = await incomeStatementService.reorderLineItems(locationId, lineItemIds)
		return { locationId, incomeStatement: updated }
	},
)

/**
 * Add a quarter to an income statement
 */
export const addQuarter = createAsyncThunk(
	'incomeStatement/addQuarter',
	async ({
		locationId,
		quarter,
	}: {
		locationId: string
		quarter: { id: string; label: string; order: number }
	}) => {
		const updated = await incomeStatementService.addQuarter(locationId, quarter)
		return { locationId, incomeStatement: updated }
	},
)

/**
 * Delete a quarter from an income statement
 */
export const deleteQuarter = createAsyncThunk(
	'incomeStatement/deleteQuarter',
	async ({ locationId, quarterId }: { locationId: string; quarterId: string }) => {
		const updated = await incomeStatementService.deleteQuarter(locationId, quarterId)
		return { locationId, incomeStatement: updated }
	},
)

// ============================================================================
// Slice
// ============================================================================

const incomeStatementSlice = createSlice({
	name: 'incomeStatement',
	initialState,
	reducers: {
		/**
		 * Clear error
		 */
		clearError: state => {
			state.error = null
		},
		/**
		 * Optimistic cell update (for immediate UI feedback)
		 */
		optimisticCellUpdate: (
			state,
			action: PayloadAction<{
				locationId: string
				lineItemId: string
				quarterId: string
				value: number
			}>,
		) => {
			const { locationId, lineItemId, quarterId, value } = action.payload
			const key = locationId
			const incomeStatement = state.entities[key]

			if (incomeStatement) {
				const lineItem = incomeStatement.lineItems.find(li => li.id === lineItemId)
				if (lineItem) {
					const valueObj = lineItem.values.find(v => v.quarter === quarterId)
					if (valueObj) {
						valueObj.value = value
					} else {
						lineItem.values.push({ quarter: quarterId, value })
					}
				}
			}
		},
	},
	extraReducers: builder => {
		// Fetch Income Statement
		builder.addCase(fetchIncomeStatement.pending, state => {
			state.isLoading = true
			state.error = null
		})
		builder.addCase(fetchIncomeStatement.fulfilled, (state, action) => {
			const { locationId, incomeStatement } = action.payload
			const key = locationId

			if (incomeStatement) {
				state.entities[key] = incomeStatement
			}

			state.isLoading = false
			state.isInitialized = true
		})
		builder.addCase(fetchIncomeStatement.rejected, (state, action) => {
			state.isLoading = false
			state.error = action.error.message || 'Failed to fetch income statement'
		})

		// Create Income Statement
		builder.addCase(createIncomeStatement.pending, state => {
			state.isLoading = true
			state.error = null
		})
		builder.addCase(createIncomeStatement.fulfilled, (state, action) => {
			const { locationId, incomeStatement } = action.payload
			const key = locationId

			state.entities[key] = incomeStatement
			state.lastSaved[key] = Date.now()
			state.isLoading = false
		})
		builder.addCase(createIncomeStatement.rejected, (state, action) => {
			state.isLoading = false
			state.error = action.error.message || 'Failed to create income statement'
		})

		// Update Income Statement
		builder.addCase(updateIncomeStatement.fulfilled, (state, action) => {
			const { locationId, incomeStatement, timestamp } = action.payload
			const key = locationId

			state.entities[key] = incomeStatement
			state.lastSaved[key] = new Date(timestamp).getTime()
		})

		// Update Cell
		builder.addCase(updateCell.fulfilled, (state, action) => {
			const { locationId, incomeStatement, timestamp } = action.payload
			const key = locationId

			state.entities[key] = incomeStatement
			state.lastSaved[key] = new Date(timestamp).getTime()
		})

		// Add Line Item
		builder.addCase(addLineItem.fulfilled, (state, action) => {
			const { locationId, incomeStatement } = action.payload
			const key = locationId

			state.entities[key] = incomeStatement
			state.lastSaved[key] = Date.now()
		})

		// Remove Line Item
		builder.addCase(removeLineItem.fulfilled, (state, action) => {
			const { locationId, incomeStatement } = action.payload
			const key = locationId

			state.entities[key] = incomeStatement
			state.lastSaved[key] = Date.now()
		})

		// Reorder Line Items
		builder.addCase(reorderLineItems.fulfilled, (state, action) => {
			const { locationId, incomeStatement } = action.payload
			const key = locationId

			state.entities[key] = incomeStatement
			state.lastSaved[key] = Date.now()
		})

		// Add Quarter
		builder.addCase(addQuarter.pending, state => {
			state.isLoading = true
			state.error = null
		})
		builder.addCase(addQuarter.fulfilled, (state, action) => {
			const { locationId, incomeStatement } = action.payload
			const key = locationId

			state.entities[key] = incomeStatement
			state.lastSaved[key] = Date.now()
			state.isLoading = false
		})
		builder.addCase(addQuarter.rejected, (state, action) => {
			state.isLoading = false
			state.error = action.error.message || 'Failed to add quarter'
		})

		// Delete Quarter
		builder.addCase(deleteQuarter.pending, state => {
			state.isLoading = true
			state.error = null
		})
		builder.addCase(deleteQuarter.fulfilled, (state, action) => {
			const { locationId, incomeStatement } = action.payload
			const key = locationId

			state.entities[key] = incomeStatement
			state.lastSaved[key] = Date.now()
			state.isLoading = false
		})
		builder.addCase(deleteQuarter.rejected, (state, action) => {
			state.isLoading = false
			state.error = action.error.message || 'Failed to delete quarter'
		})
	},
})

// ============================================================================
// Exports
// ============================================================================

export const { clearError, optimisticCellUpdate } = incomeStatementSlice.actions

export default incomeStatementSlice.reducer

// Selectors
export const selectIncomeStatement = (state: any, locationId: string): IncomeStatement | null => {
	if (!state.incomeStatement?.entities) return null
	const key = locationId
	return state.incomeStatement.entities[key] || null
}

export const selectIsLoading = (state: any): boolean => {
	return state.incomeStatement?.isLoading ?? false
}

export const selectError = (state: any): string | null => {
	return state.incomeStatement?.error ?? null
}

export const selectLastSaved = (state: any, locationId: string): Date | null => {
	if (!state.incomeStatement?.lastSaved) return null
	const key = locationId
	const timestamp = state.incomeStatement.lastSaved[key]
	return timestamp ? new Date(timestamp) : null
}
